import { startCase } from "lodash-es";
import { getChartColor } from "@/composables/chartPalette";
import type { TranslationFn } from "@/utils/registries";
import type {
  RawDataItem,
  StackOption,
  StackableStyleConfig,
  ValueLabelFn,
} from "../types/ChartData";

/**
 * Locale-aware natural order ("9" before "10"). The stacked chart's axis and legend and the
 * breakdown bar all sort with it, so they list values in one order.
 */
export const naturalCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

/**
 * Builds a stack option for `fieldName`, colouring every value present in `rawData` (or using
 * `explicitStyleMap`). The field name's literal type is kept so callers can narrow on it.
 */
export const createStackOption = <F extends string>(
  rawData: readonly RawDataItem[],
  fieldName: F,
  config?: {
    explicitStyleMap?: { [key: string]: StackableStyleConfig };
    valueTranslationKeyPrefix?: string; // Optional prefix for translating values
    valueLabelFn?: ValueLabelFn;
  }
): StackOption<F> => {
  // String() maps null to "null" — the same key the chart groups null values under —
  // so nullable fields get a stable styleMap entry (and can hit semantic overrides).
  const uniqueValues = [
    ...new Set(rawData.map((item) => String(item[fieldName]))),
  ].sort(); // Sort for consistent color mapping

  let styleMap: { [key: string]: StackableStyleConfig };
  const explicitStyleMap = config?.explicitStyleMap;
  const valueTranslationKeyPrefix = config?.valueTranslationKeyPrefix;
  const valueLabelFn = config?.valueLabelFn;

  if (explicitStyleMap) {
    styleMap = explicitStyleMap;
  } else {
    styleMap = {};
    uniqueValues.forEach((name, index) => {
      styleMap[name] = {
        backgroundColor: getChartColor({
          field: fieldName,
          value: name,
          index,
        }),
      };
    });
  }

  // Generate fallback label and i18n key
  const label = startCase(fieldName);
  const labelKey = `charts.fields.${fieldName}`;

  return {
    fieldName: fieldName,
    label: label, // Fallback label
    labelKey: labelKey,
    styleMap: styleMap,
    valueTranslationKeyPrefix: valueTranslationKeyPrefix,
    valueLabelFn: valueLabelFn,
  };
};

/**
 * Label of one stack value, shared by the stacked chart's legend and the breakdown bar so they
 * agree: "Unknown" for rows without a value (as the table's filter chips name them), else the
 * option's `valueLabelFn` when set, else the value's translation under
 * `valueTranslationKeyPrefix`, else the value itself. A value with no translation shows as
 * itself rather than as its i18n key.
 *
 * @param value - The stringified value, as the option's `styleMap` keys it ("null" for null).
 */
export const stackValueLabel = (
  stackOption: StackOption,
  value: string,
  t: TranslationFn
): string => {
  if (value === "null") return t("common.unknown");
  if (stackOption.valueLabelFn) return stackOption.valueLabelFn(value, t);
  if (stackOption.valueTranslationKeyPrefix) {
    const key = `${stackOption.valueTranslationKeyPrefix}.${value}`;
    const translated = t(key);
    return translated === key ? value : translated;
  }
  return value;
};
